import MafiaKillInitiatorAbi from "../../abis/MafiaKillInitiator.json";
import config from "../../config/config";
import { BackfireMode } from "../../constants/backfire";
import { KillAttackResult } from "../../constants/killInitiation";
import { Messages } from "../../constants/messages";
import {
  field,
  getDecodedEvent,
  parseEther,
  readContract,
  writeContractAndWait,
} from "../viem";

const getAddress = () => {
  if (!config.killInitiatorAddress) {
    throw new Error("Kill initiator contract is not configured");
  }
  return config.killInitiatorAddress;
};

/** Admin-configured max |killerRank - victimRank| allowed for an attack. */
export const getMaxRankDifference = async (): Promise<number> => {
  const value = await readContract<string>({
    address: getAddress(),
    abi: MafiaKillInitiatorAbi,
    functionName: "maxRankDifference",
  });
  const maxRankDifference = Number(value);

  if (!Number.isFinite(maxRankDifference)) {
    throw new Error("Invalid maxRankDifference from contract");
  }

  return maxRankDifference;
};

export interface AttackInitiatedEvent {
  killer: string;
  victim: string;
  bulletAmount: string;
  result: KillAttackResult;
  timestamp: number;
  killerWords: string;
  transactionHash: string;
}

const parseAttackResult = (value: unknown): KillAttackResult => {
  const numeric = Number(value);
  if (
    Number.isFinite(numeric) &&
    numeric >= KillAttackResult.Attempted &&
    numeric <= KillAttackResult.NoWeaponEquipped
  ) {
    return numeric as KillAttackResult;
  }
  return KillAttackResult.Attempted;
};

export const getAttackResultMessage = (
  result: KillAttackResult,
  cityName?: string,
  maxRankDifference?: number | null
): string => {
  switch (result) {
    case KillAttackResult.Attempted:
      return Messages.KILL_INITIATION.ATTACK_ATTEMPTED;
    case KillAttackResult.BulletAmountOutOfRange:
      return Messages.KILL_INITIATION.BULLET_AMOUNT_OUT_OF_RANGE;
    case KillAttackResult.VictimInSafehouse:
      return Messages.KILL_INITIATION.TARGET_IN_SAFEHOUSE;
    case KillAttackResult.VictimInactive:
      return Messages.KILL_INITIATION.VICTIM_INACTIVE;
    case KillAttackResult.RankDifferenceOutOfRange:
      return maxRankDifference != null
        ? Messages.KILL_INITIATION.RANK_DIFFERENCE_OUT_OF_RANGE.replace(
            "{max}",
            String(maxRankDifference)
          )
        : "Rank difference is too large to attack this target.";
    case KillAttackResult.CityMismatch:
      return Messages.KILL_INITIATION.CITY_MISMATCH;
    case KillAttackResult.DetectiveLockExpired:
      return Messages.KILL_INITIATION.DETECTIVE_LOCK_EXPIRED;
    case KillAttackResult.NoWeaponEquipped:
      return Messages.KILL_INITIATION.NO_WEAPON_EQUIPPED.replace(
        "{city}",
        cityName || "your city"
      );
    default:
      return Messages.KILL_INITIATION.ATTACK_ATTEMPTED;
  }
};

/** Configure the connected user's automatic backfire response. */
export const setBackfire = async (
  account: string,
  policy: BackfireMode,
  fixedAmount = 0
): Promise<{ transactionHash: string }> => {
  const fixedAmountWei =
    policy === BackfireMode.Range
      ? parseEther(Number(fixedAmount).toFixed(18))
      : 0n;

  const tx = await writeContractAndWait({
    address: getAddress(),
    abi: MafiaKillInitiatorAbi,
    functionName: "setBackfire",
    args: [policy, fixedAmountWei],
    account,
    gasMultiplier: 1.5,
  });

  return { transactionHash: String(tx.transactionHash) };
};

/** Initiate an attack and return the contract's soft-failure result. */
export const initiateAttack = async (
  account: string,
  victim: string,
  bulletAmount: number,
  killerWords = ""
): Promise<AttackInitiatedEvent> => {
  if (!config.killInitiationContractEnabled) {
    throw new Error("Kill initiation contract calls are disabled");
  }

  const tx = await writeContractAndWait({
    address: getAddress(),
    abi: MafiaKillInitiatorAbi,
    functionName: "initiateAttack",
    args: [
      victim,
      parseEther(Number(bulletAmount).toFixed(18)),
      killerWords.trim(),
    ],
    account,
    gasMultiplier: 1.5,
  });

  const event = getDecodedEvent(tx, MafiaKillInitiatorAbi, "AttackInitiated");
  if (!event) {
    throw new Error(
      "Attack transaction confirmed but AttackInitiated event was not found"
    );
  }

  return {
    killer: String(field(event, "killer", 0) ?? ""),
    victim: String(field(event, "victim", 1) ?? ""),
    bulletAmount: String(field(event, "bulletAmount", 2) ?? "0"),
    result: parseAttackResult(field(event, "result", 3)),
    timestamp: Number(field(event, "timestamp", 4) ?? 0),
    killerWords: String(
      field(event, "killerWords", 5) ?? field(event, "message", 5) ?? ""
    ),
    transactionHash: String(tx.transactionHash),
  };
};
